import { useScreenWidth } from "@msys/ui";
import { Box, Stack } from "@mui/material";
import type React from "react";
import { ONBOARDING_COLORS } from "../../features/organisations/onboarding/onboardingDesign.js";
import {
  SignupBrandPanel,
  SignupLogo,
  type BrandPanelRole,
} from "./SignupBrandPanel.js";
import { SignupLanguageSelector } from "./SignupLanguageSelector.js";

interface Props {
  /** Decides the copy of the brand panel. */
  signupRole: BrandPanelRole;
  /** Widest the content column may get. */
  contentMaxWidth?: number;
  /** Pinned to the bottom of the content pane, outside the centred column. */
  footer?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * The split layout of `/` and `/signup`: the brand panel beside the content,
 * collapsing to a brand header with the logo on narrow viewports, and the
 * language switch top right.
 */
export const SignupSplitLayout = ({
  signupRole,
  contentMaxWidth = 480,
  footer,
  children,
}: Props) => {
  const { isMinTablet } = useScreenWidth();

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: isMinTablet ? "row" : "column",
        maxWidth: "100%",
        minWidth: 0,
        minHeight: "100%",
      }}
    >
      {isMinTablet ? (
        <SignupBrandPanel signupRole={signupRole} />
      ) : (
        <Box
          component="header"
          sx={{
            bgcolor: ONBOARDING_COLORS.brandPanel,
            px: 2,
            pt: "calc(env(safe-area-inset-top) + 14px)",
            pb: "14px",
          }}
        >
          <SignupLogo />
        </Box>
      )}
      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          bgcolor: "common.white",
          px: { xs: 2, tablet: 6, desktop: "96px" },
          pt: isMinTablet ? "32px" : "12px",
          pb: "calc(env(safe-area-inset-bottom) + 40px)",
        }}
      >
        <Stack
          direction="row"
          sx={{ alignItems: "flex-start", justifyContent: "flex-end" }}
        >
          <SignupLanguageSelector />
        </Stack>
        {/* The content is centred between the language switch and the footer. */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            width: "100%",
            maxWidth: contentMaxWidth,
            mx: "auto",
            py: 2,
          }}
        >
          {children}
        </Box>
        {footer}
      </Box>
    </Box>
  );
};
